import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Layout } from './components/Layout';
import { OrganizationGate } from './components/OrganizationGate';
import { ProtectedRoute } from './components/ProtectedRoute';
import { FullScreenLoader } from './components/ui/feedback';
import { appRoutes, legacyToAppPath } from './lib/routes';
import CallbackPage from './routes/callback';
import LandingPage from './routes/index';

// Landing and auth callback stay in the entry chunk; every other page loads on demand.
const AppDashboardPage = lazy(() => import('./routes/app/index'));
const DatenschutzPage = lazy(() => import('./routes/datenschutz/index'));
const DemoRequestPage = lazy(() => import('./routes/demo/index'));
const EventDetailPage = lazy(() => import('./routes/events/$eventId'));
const EventsPage = lazy(() => import('./routes/events/index'));
const HelpArticlePage = lazy(() => import('./routes/hilfe/$articleSlug'));
const HelpPage = lazy(() => import('./routes/hilfe/index'));
const MemberDetailPage = lazy(() => import('./routes/members/$memberId'));
const MembersPage = lazy(() => import('./routes/members/index'));
const NewsDetailPage = lazy(() => import('./routes/news/$newsId'));
const NewsPage = lazy(() => import('./routes/news/index'));
const PlatformAdminPage = lazy(() => import('./routes/plattform-admin/index'));
const ProfilePage = lazy(() => import('./routes/profile/index'));
const BexioCallbackPage = lazy(() => import('./routes/verwaltung/bexio/callback'));
const AdminPage = lazy(() =>
  import('./components/admin/AdminContent').then((module) => ({ default: module.AdminContent })),
);

export default function App() {
  return (
    <Suspense fallback={<FullScreenLoader />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/callback" element={<CallbackPage />} />
        <Route path="/hilfe" element={<HelpPage />} />
        <Route path="/hilfe/:articleSlug" element={<HelpArticlePage />} />
        <Route path="/demo" element={<DemoRequestPage />} />
        <Route path="/datenschutz" element={<DatenschutzPage />} />

        <Route path={appRoutes.dashboard} element={<ProtectedRoute />}>
          {/* Organization-scoped pages require a member record in the active organization. */}
          <Route element={<OrganizationGate />}>
            <Route element={<Layout />}>
              <Route index element={<AppDashboardPage />} />
              <Route path="events" element={<EventsPage />} />
              <Route path="events/:eventId" element={<EventDetailPage />} />
              <Route path="members" element={<MembersPage />} />
              <Route path="members/:memberId" element={<MemberDetailPage />} />
              <Route path="news" element={<NewsPage />} />
              <Route path="news/:newsId" element={<NewsDetailPage />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="verwaltung" element={<AdminPage />} />
            </Route>
          </Route>

          {/* Platform admins and the Bexio OAuth return may not have an organization membership. */}
          <Route element={<Layout />}>
            <Route path="verwaltung/bexio/callback" element={<BexioCallbackPage />} />
            <Route path="plattform-admin" element={<PlatformAdminPage />} />
          </Route>

          <Route path="*" element={<Navigate to={appRoutes.dashboard} replace />} />
        </Route>

        <Route path="/events/*" element={<LegacyRedirect />} />
        <Route path="/members/*" element={<LegacyRedirect />} />
        <Route path="/news/*" element={<LegacyRedirect />} />
        <Route path="/profile" element={<LegacyRedirect />} />
        <Route path="/verwaltung/*" element={<LegacyRedirect />} />
        <Route path="/plattform-admin" element={<LegacyRedirect />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function LegacyRedirect() {
  const location = useLocation();
  const nextPath = `${legacyToAppPath(location.pathname)}${location.search}${location.hash}`;
  return <Navigate to={nextPath} replace />;
}
